/*
API client for HTTP requests with JWT authentication.
Features:
- Proactive token refresh before expiry
- Automatic retry on 401 with refresh token
- Graceful fallback to anonymous on auth failure
*/
import axios from 'axios'
import { clearAuthData } from './authStorage.js'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const api = axios.create({
  baseURL: API_URL,
  headers: {},
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false
let refreshPromise = null

/**
 * Decode JWT token to get expiry time
 */
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 // Convert to milliseconds
  } catch {
    return 0
  }
}

/**
 * Check if token will expire within the given buffer time (in milliseconds)
 */
function isTokenExpiringSoon(token, bufferMs = 30 * 60 * 1000) { // 30 minutes buffer
  if (!token) return true
  const expiry = getTokenExpiry(token)
  return Date.now() > expiry - bufferMs
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
    refresh: refreshToken,
  })

  const { access, refresh } = response.data
  localStorage.setItem('access_token', access)

  // If server returns a new refresh token (rotation enabled), save it
  if (refresh) {
    localStorage.setItem('refresh_token', refresh)
  }

  return access
}

// Request interceptor - proactively refresh token if expiring soon
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('access_token')

    // If we have a token and it's expiring soon, refresh it proactively
    if (token && isTokenExpiringSoon(token)) {
      // Prevent multiple simultaneous refresh calls
      if (!isRefreshing) {
        isRefreshing = true
        refreshPromise = refreshAccessToken()
          .catch((err) => {
            console.warn('Proactive token refresh failed:', err.message)
            return null // Return null to signal failure
          })
          .finally(() => {
            isRefreshing = false
          })
      }

      try {
        const newToken = await refreshPromise
        if (newToken) {
          token = newToken
        }
      } catch {
        // Ignore - we'll use the old token and let the response interceptor handle 401
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle 401 errors with retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const newToken = await refreshAccessToken()
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        } catch {
          // Refresh failed; retry anonymously below.
        }
      }
      clearAuthData()
      delete originalRequest.headers.Authorization
      return api(originalRequest)
    }

    return Promise.reject(error)
  }
)

export default api
